/**
 * Decision 0119: project settings on a card. The lead proposes (`propose_settings`), the project's owner or a workspace
 * admin decides; the card's option carries `ChangeProjectSettings`, applied as whoever answers. Pure: the values a
 * card was proposed against (`settingsBefore`), the stale check, and the readable diff the card shows.
 */
import type * as P from "@minga/protocol"
import { AGENT_LABEL } from "@minga/protocol"
import { canonicalJson } from "./hash"
import { areaOf, leadOf } from "./state"
import type { ProjectState } from "./types"

export type ChangeProjectSettings = Extract<P.ProjectSettingsEffect, { _tag: "ChangeProjectSettings" }>
export type SettingsKey = keyof P.ProjectSettingsPatch

/** The settings a card may change, in display order. Never the allowlist, repos, status, policies or members. */
export const SETTINGS_KEYS: ReadonlyArray<SettingsKey> = [
  "routing",
  "workers",
  "wip",
  "cadence",
  "baseBranches",
]

type Project = NonNullable<ProjectState["project"]>

/** The project's current value of one settings key (null = not set). */
const currentOf = (p: Project, k: SettingsKey): unknown => {
  switch (k) {
    case "routing":
      return p.routing ?? null
    case "workers":
      return p.workers ?? null
    case "wip":
      return p.wip
    case "cadence":
      return p.cadence
    case "baseBranches":
      return p.baseBranches ?? null
  }
}

/** Only the keys a settings card may carry (anything else in a decoded patch is dropped). */
export const settingsPatchOf = (patch: P.ProjectSettingsPatch): P.ProjectSettingsPatch => {
  const out: Record<string, unknown> = {}
  for (const k of SETTINGS_KEYS) if (patch[k] !== undefined) out[k] = patch[k]
  return out as P.ProjectSettingsPatch
}

/** The current values of the keys `patch` changes: the card's `before`. */
export const settingsBefore = (s: ProjectState, patch: P.ProjectSettingsPatch): P.ProjectSettingsBefore => {
  const p = s.project
  const out: Record<string, unknown> = {}
  if (!p) return out as P.ProjectSettingsBefore
  for (const k of SETTINGS_KEYS) if (patch[k] !== undefined) out[k] = currentOf(p, k)
  return out as P.ProjectSettingsBefore
}

const KEY_WORDS: Readonly<Record<SettingsKey, string>> = {
  routing: "the routing rules",
  workers: "the worker presets",
  wip: "the project's WIP",
  cadence: "the time zone and daily check",
  baseBranches: "the base branches",
}

/** Why the card no longer applies as proposed (a value changed since it opened), or null. */
export const settingsStaleProblem = (s: ProjectState, e: ChangeProjectSettings): string | null => {
  const p = s.project
  if (!p) return "the project does not exist yet"
  const before = e.before as Record<string, unknown>
  for (const k of SETTINGS_KEYS) {
    if (e.patch[k] === undefined) continue
    if (canonicalJson(before[k] ?? null) !== canonicalJson(currentOf(p, k)))
      return `${KEY_WORDS[k]} changed since this card opened: propose again from the current settings`
  }
  for (const a of e.areaWip ?? []) {
    const area = areaOf(s, a.areaId)
    if (!area) return `area ${a.areaId} does not exist`
    if (area.wip.maxNow !== a.before)
      return `${area.name}'s WIP changed since this card opened: propose again from the current settings`
  }
  return null
}

/** Why an agent may not open this settings card, or null: the project lead proposes, with something to change. */
export const settingsProposalProblem = (
  s: ProjectState,
  agentId: string,
  e: ChangeProjectSettings,
): string | null => {
  if (leadOf(s)?.id !== agentId)
    return "project settings are proposed by the project lead: ask the lead (an area lead's or role agent's suggestion goes through the lead)"
  const patch = settingsPatchOf(e.patch)
  if (Object.keys(patch).length === 0 && (e.areaWip ?? []).length === 0)
    return "a settings card changes at least one setting"
  for (const a of e.areaWip ?? []) {
    const area = areaOf(s, a.areaId)
    if (area?.status !== "active") return `area ${a.areaId} is not an active area of this project`
    if (!Number.isInteger(a.maxNow) || a.maxNow < 1 || a.maxNow > 10)
      return `${area.name}'s WIP: 1–10 Now items`
  }
  return settingsStaleProblem(s, e)
}

// ---- the readable diff ---------------------------------------------------------------------------------------------

/** One row of a settings card: what changes, from what, to what ("—" = not set). */
export interface SettingsDiffRow {
  readonly label: string
  readonly before: string
  readonly after: string
}

const NONE = "—"

const modelsText = (by: P.ModelsByAgent | undefined): string => {
  const parts: string[] = []
  for (const k of Object.keys(AGENT_LABEL) as Array<P.AgentKind>) {
    const m = by?.[k]
    if (!m || (m.model === null && m.effort === null)) continue
    parts.push(`${AGENT_LABEL[k]} ${[m.model, m.effort].filter((x) => x !== null).join(" ")}`)
  }
  return parts.join(", ")
}

/** A routing rule in words: "worker, required: Claude Code or Codex (Codex gpt-5 high)". */
export const routingRuleText = (r: P.RoutingRule): string => {
  // any-of CLIs (a later RoutingRule.agents) when present, else the one `agent`
  const many = (r as { readonly agents?: ReadonlyArray<P.AgentKind> | null }).agents
  const clis =
    many && many.length > 0
      ? many.map((a) => AGENT_LABEL[a] ?? a)
      : r.agent !== null
        ? [AGENT_LABEL[r.agent] ?? r.agent]
        : []
  const who =
    clis.length === 0
      ? "any CLI"
      : clis.length === 1
        ? clis[0]
        : `${clis.slice(0, -1).join(", ")} or ${clis.at(-1)}`
  const models = modelsText(r.byAgent)
  const where = [
    r.runnerId ? `runner ${r.runnerId}` : null,
    r.profileId ? `profile ${r.profileId}` : null,
    r.apiModel ? `API model ${r.apiModel}` : null,
  ].filter((x) => x !== null)
  const types = r.itemTypes && r.itemTypes.length > 0 ? ` for ${r.itemTypes.join(", ")}` : ""
  return `${r.kind}${types}, ${r.mode}: ${who}${models ? ` (${models})` : ""}${where.length ? `, ${where.join(", ")}` : ""}`
}

const presetText = (x: P.WorkerPreset): string => {
  const models = modelsText(x as unknown as P.ModelsByAgent)
  return [
    x.agent ? AGENT_LABEL[x.agent] : "any CLI",
    models || null,
    x.repo === "none" ? "no repo" : null,
    x.access.mcp ? `MCP ${x.access.mcp}` : null,
  ]
    .filter((v) => v !== null)
    .join(", ")
}

const capText = (n: number | null | undefined): string => (n === undefined || n === null ? "none" : String(n))

const hourText = (h: number | null | undefined): string =>
  h === null ? "off" : `${String(h ?? 9).padStart(2, "0")}:00`

/** Rows for keyed lists: one per key added, removed or changed. */
const keyedRows = <T>(
  label: (key: string, item: T) => string,
  text: (item: T) => string,
  before: ReadonlyArray<T>,
  after: ReadonlyArray<T>,
  keyOf: (item: T) => string,
): SettingsDiffRow[] => {
  const rows: SettingsDiffRow[] = []
  const old = new Map(before.map((x) => [keyOf(x), x]))
  const now = new Map(after.map((x) => [keyOf(x), x]))
  for (const x of after) {
    const k = keyOf(x)
    const was = old.get(k)
    if (!was) rows.push({ label: label(k, x), before: NONE, after: text(x) })
    else if (canonicalJson(was) !== canonicalJson(x))
      rows.push({ label: label(k, x), before: text(was), after: text(x) })
  }
  for (const x of before)
    if (!now.has(keyOf(x))) rows.push({ label: label(keyOf(x), x), before: text(x), after: "removed" })
  return rows
}

/**
 * The card's diff, row by row, against the values it was proposed with (`before`), so a decided card still reads as
 * it was. `repoName` names a repo id (the cloud knows remotes; default the id).
 */
export const settingsDiffRows = (
  s: ProjectState,
  e: ChangeProjectSettings,
  repoName: (id: string) => string = (id) => id,
): ReadonlyArray<SettingsDiffRow> => {
  const rows: SettingsDiffRow[] = []
  const b = e.before
  const p = e.patch
  if (p.routing !== undefined) {
    const before = b.routing?.rules ?? []
    const r = keyedRows(
      (_, x: P.RoutingRule) => `Routing rule “${x.label}”`,
      routingRuleText,
      before,
      p.routing.rules,
      (x) => x.key,
    )
    // an order change alone matters (the first matching rule applies)
    const order = (xs: ReadonlyArray<P.RoutingRule>) => xs.map((x) => x.key).join(",")
    if (r.length === 0 && order(before) !== order(p.routing.rules))
      r.push({
        label: "Routing rule order",
        before: before.map((x) => x.label).join(" → "),
        after: p.routing.rules.map((x) => x.label).join(" → "),
      })
    rows.push(...r)
  }
  if (p.workers !== undefined) {
    const before = b.workers ?? null
    rows.push(
      ...keyedRows(
        (_, x: P.WorkerPreset) => `Worker preset “${x.label}”`,
        presetText,
        before?.presets ?? [],
        p.workers.presets,
        (x) => x.key,
      ),
    )
    if (capText(before?.attemptCap) !== capText(p.workers.attemptCap))
      rows.push({
        label: "Attempts per item",
        before: capText(before?.attemptCap),
        after: capText(p.workers.attemptCap),
      })
    if (capText(before?.noProgressCap) !== capText(p.workers.noProgressCap))
      rows.push({
        label: "Attempts without progress",
        before: capText(before?.noProgressCap),
        after: capText(p.workers.noProgressCap),
      })
  }
  if (p.wip !== undefined && b.wip?.maxNow !== p.wip.maxNow)
    rows.push({
      label: "Project WIP (Now items)",
      before: String(b.wip?.maxNow ?? NONE),
      after: String(p.wip.maxNow),
    })
  for (const a of e.areaWip ?? [])
    if (a.before !== a.maxNow)
      rows.push({
        label: `${areaOf(s, a.areaId)?.name ?? "Area"} WIP (Now items)`,
        before: String(a.before),
        after: String(a.maxNow),
      })
  if (p.cadence !== undefined) {
    if (b.cadence?.timezone !== p.cadence.timezone)
      rows.push({ label: "Time zone", before: b.cadence?.timezone ?? NONE, after: p.cadence.timezone })
    if (hourText(b.cadence?.dailyCheckHour) !== hourText(p.cadence.dailyCheckHour))
      rows.push({
        label: "Daily check",
        before: hourText(b.cadence?.dailyCheckHour),
        after: hourText(p.cadence.dailyCheckHour),
      })
  }
  if (p.baseBranches !== undefined)
    rows.push(
      ...keyedRows(
        (k) => `Base branch of ${repoName(k)}`,
        (x: P.RepoBaseBranch) => x.branch,
        b.baseBranches ?? [],
        p.baseBranches,
        (x) => x.repoId,
      ).map((r) => (r.after === "removed" ? { ...r, after: "the repo's default branch" } : r)),
    )
  return rows
}

/** The diff as text lines ("Time zone: UTC → Europe/Berlin"), for the card's prompt and the agent's answer. */
export const settingsDiffText = (rows: ReadonlyArray<SettingsDiffRow>): string =>
  rows.length === 0 ? "no change" : rows.map((r) => `- ${r.label}: ${r.before} → ${r.after}`).join("\n")

/** The `ChangeProjectSettings` effect of a question's options, if it carries one (a settings card). */
export const settingsEffectOf = (body: {
  readonly _tag: string
  readonly options?: ReadonlyArray<{ readonly key: string; readonly effects: ReadonlyArray<P.OptionEffect> }>
}): { readonly key: string; readonly effect: ChangeProjectSettings } | null => {
  if (body._tag !== "question") return null
  for (const o of body.options ?? [])
    for (const e of o.effects) if (e._tag === "ChangeProjectSettings") return { key: o.key, effect: e }
  return null
}
